import React, { useEffect, useState } from 'react'

import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/ui/atoms/dialog/Dialog'
import { FormControl } from '@/ui/atoms/form/Form'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { Typography } from '@/ui/atoms/typography/Typography'
import { cn } from '@/ui/utils/style'
import { testIds } from '@/ui/utils/testIds'
import { getDisplayToken } from '@/domain/hooks/useDisplayToken'
import { useChainId } from 'wagmi'
import { getChainConfigEntry } from '@/config/chain'
import { SearchInput } from '@/ui/atoms/search-input/SearchInput'
import { normalizeForSearch } from '@/utils/strings'
import EvmIcon from './EvmIcon'
import CoreIcon from './CoreIcon'
import { TokenWithBalance } from '@/domain/common/types'
import { BridgeToken } from '../../logic/useTrade'
import { ChevronDown } from 'lucide-react'
import { truncateWithEllipsis } from '@/domain/common/format'
import { L1Badge } from '@/ui/molecules/l1-badge/L1Badge'

const HEADER_HEIGHT = 148
const LIST_FALLBACK_HEIGHT = 300
const DEBOUNCE_TIME = 200

const networkIcons = {
  Core: CoreIcon,
  EVM: EvmIcon,
}

interface SearchableAssetSelectorProps {
  assets: (TokenWithBalance | BridgeToken)[]
  selectedAsset?: TokenWithBalance | BridgeToken
  setSelectedAsset?: (newSymbol: TokenSymbol, isL1: boolean) => void
  withFormControl?: boolean
  disabled?: boolean
  resetBorrowStatus?: () => void
  swapMode?: string
  isDestination?: boolean
  featuredAssets?: string[]
}

type NetworkTypes = 'All' | 'EVM' | 'Core'

export function SearchableAssetSelector({
  assets,
  selectedAsset,
  setSelectedAsset,
  withFormControl,
  disabled,
  resetBorrowStatus,
}: SearchableAssetSelectorProps) {
  const chainId = useChainId()
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedNetwork, setSelectedNetwork] = useState<NetworkTypes>('All')
  const Wrapper = withFormControl ? FormControl : React.Fragment
  const { nativeAssetInfo } = getChainConfigEntry(chainId).markets || {}
  const [listHeight, setListHeight] = useState(0)
  const readonly = assets.length <= 1

  const filteredAssets = assets.filter((asset) => {
    const normalizedSymbol = normalizeForSearch(asset.token.symbol)
    const normalizedName = normalizeForSearch(asset.token.name)
    const normalizedQuery = normalizeForSearch(searchQuery)

    // Filter by network type
    if (selectedNetwork === 'Core') {
      // Only show L1 tokens for Core
      if (!('tokenId' in asset)) return false
    } else if (selectedNetwork === 'EVM') {
      // Only show non-L1 tokens for EVM
      if ('tokenId' in asset) return false
    }

    return normalizedSymbol.includes(normalizedQuery) || normalizedName.includes(normalizedQuery)
  })

  function handleSelect(symbol: string, isL1: boolean) {
    if (disabled && resetBorrowStatus) {
      resetBorrowStatus()
    }
    setSelectedAsset?.(TokenSymbol(symbol), isL1)
    setOpen(false)
  }

  function handleNetworkSelect(network: NetworkTypes) {
    setSelectedNetwork(network)
  }

  function handleOnOpenChange(open: boolean) {
    setOpen(open)
    setSearchQuery('')
  }

  // Ensure scrollable list takes into account mobile keyboard height
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    function handleResize() {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      timeoutId = setTimeout(() => {
        const visibleHeight = window.visualViewport?.height || window.innerHeight
        setListHeight(visibleHeight - HEADER_HEIGHT)
      }, DEBOUNCE_TIME)
    }

    if (open) {
      const visibleHeight = window.visualViewport?.height || window.innerHeight
      setListHeight(visibleHeight - HEADER_HEIGHT)
      window.visualViewport?.addEventListener('resize', handleResize)
      return () => {
        window.visualViewport?.removeEventListener('resize', handleResize)
        if (timeoutId) clearTimeout(timeoutId)
      }
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [open])

  return (
    <Dialog open={open} onOpenChange={handleOnOpenChange}>
      <DialogTrigger
        className={cn(
          'flex h-14 w-36 items-center justify-between rounded-sm border border-input bg-input px-4 py-4',
          'ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          disabled && 'cursor-not-allowed opacity-70',
          readonly && 'pointer-events-none',
        )}
        disabled={(disabled && !resetBorrowStatus) || readonly}
        data-testid={testIds.component.AssetSelector.trigger}
        aria-label="Asset selector"
      >
        <Wrapper>
          <div className="flex flex-row items-center gap-2">
            {selectedAsset && (
              <div className="relative">
                <TokenIcon
                  token={selectedAsset.token}
                  isL1={'tokenId' in selectedAsset}
                  disableEulerFallback
                  className="h-6 w-6"
                />
                {'tokenId' in selectedAsset && <L1Badge />}
              </div>
            )}
            <Typography className="whitespace-nowrap text-foreground">
              <span title={selectedAsset?.token.symbol ?? ''}>
                {truncateWithEllipsis(selectedAsset?.token.symbol ?? '', 8)}
              </span>
            </Typography>
          </div>
        </Wrapper>
        {!readonly && <ChevronDown className="h-5 w-5 text-icon-foreground" />}
      </DialogTrigger>
      <DialogContent
        className="md:data-[state=closed]:slide-out-to-top-[16px] md:data-[state=open]:slide-in-from-top-[16px] border border-border bg-background pt-6 md:top-24 md:min-w-0 md:max-w-md md:pb-2"
        aria-describedby={undefined}
        contentVerticalPosition="top"
        overlayClassName="z-[240]"
        closeClassName="top-6"
      >
        <div className="mb-4">
          <DialogTitle>
            <Typography variant="p" className="font-bold text-lg" gradient>
              Select token
            </Typography>
          </DialogTitle>
          <SearchInput
            value={searchQuery}
            placeholder="Search tokens..."
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div
          className="space-y-2 overflow-y-auto md:max-h-80"
          style={{ height: Math.max(listHeight, LIST_FALLBACK_HEIGHT) }}
        >
          <>
            <div className="flex flex-row flex-wrap gap-2">
              {(['All', 'EVM', 'Core'] as NetworkTypes[]).map((network) => {
                return (
                  <div className={cn(selectedNetwork === network ? 'box-gradient' : '')} key={`featured-${network}`}>
                    <button
                      onClick={() => handleNetworkSelect(network)}
                      className={cn(
                        'flex flex-row items-center gap-2 rounded-xs border-2',
                        selectedNetwork === network ? 'border-border bg-accent' : '',
                      )}
                    >
                      {network === 'All' ? (
                        <p className="px-3 py-1 text-foreground">All</p>
                      ) : (
                        <>
                          {networkIcons?.[network as keyof typeof networkIcons] && (
                            <div>{networkIcons?.[network as keyof typeof networkIcons]}</div>
                          )}
                        </>
                      )}
                    </button>
                  </div>
                )
              })}
            </div>
          </>

          <Typography variant="p" className="pt-3 font-semibold">
            Tokens
          </Typography>

          {filteredAssets.map((a) => {
            const displayToken =
              a.token.symbol === nativeAssetInfo?.wrappedNativeAssetSymbol ? a.token : getDisplayToken(chainId, a.token)

            const isL1 = 'tokenId' in a

            return (
              <button
                key={`${a.token.address}-${a.token.symbol}-${isL1}`}
                className="flex w-full items-center rounded-sm p-3 transition-colors hover:bg-muted"
                onClick={() => handleSelect(a.token.symbol, isL1)}
                data-testid={testIds.component.AssetSelector.option}
              >
                <div className={cn('flex w-full flex-row justify-between', a.balance && 'w-full')}>
                  <div className="flex flex-row items-center gap-3">
                    <div className="relative">
                      <TokenIcon token={displayToken} isL1={isL1} disableEulerFallback className="h-6 w-6" />
                      {isL1 && <L1Badge variant="primary" />}
                    </div>
                    <div className="flex flex-col text-left">
                      <div className="flex flex-row items-center gap-2">
                        <Typography className="font-semibold">{displayToken.symbol}</Typography>
                        <Typography variant="prompt">{displayToken.name}</Typography>
                      </div>

                      <Typography variant="prompt">On {isL1 ? 'HyperCore' : 'HyperEVM'}</Typography>
                    </div>
                  </div>
                  <div className="flex flex-row items-center">
                    <div className="flex flex-col items-end gap-1">
                      {a.token.unitPriceUsd.gt(0) && a.balance.gt(0) && (
                        <Typography className="text-sm">{a.token.formatUSD(a.balance)}</Typography>
                      )}

                      {a.balance.gt(0) && (
                        <Typography variant="prompt">{a.token.format(a.balance, { style: 'compact' })}</Typography>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            )
          })}
          {filteredAssets.length === 0 && (
            <div className="p-4 text-center">
              <Typography variant="prompt">No assets found</Typography>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
